'use client';

import { CountryCode, getPhoneState, PhoneInput } from '@repo/features/phone-input';
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel, InputGroupAddon, Spinner } from '@repo/ui/components';
import { cn } from '@repo/ui/lib/utils';
import { formatPhoneToInternational, toCountryCode } from '@repo/sdk/helpers/phone';
import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { ReadOnlyTooltip, useReadOnly } from '../../components/readonly.tsx';
import { dedupeErrors } from '../../lib/dedupe-errors.ts';
import { withFieldGroup } from '../../lib/app-form-hook.tsx';
const defaultValues = {
  phone: '',
  phone_country: ''
};
type PhoneFieldRenderProps = {
  allowInvalidPhoneSubmit?: boolean;
  className?: string;
  compact?: boolean;
  'data-testid'?: string;
  defaultCountry?: CountryCode | null;
  description?: ReactNode | string;
  disabled?: boolean;
  inputClassName?: string;
  inputVariant?: 'default' | 'ghost';
  isPending?: boolean;
  label?: ReactNode | string;
  placeholder?: string;
  required?: boolean;
  showChevron?: boolean;
  showCode?: boolean;
  showCopy?: boolean;
  syncCountryWithDefault?: boolean;
  variant?: 'button-group' | 'input-group';
};
const phoneFieldProps: PhoneFieldRenderProps = {};
export const PhoneField = withFieldGroup({
  defaultValues,
  props: phoneFieldProps,
  render: function Render({
    allowInvalidPhoneSubmit,
    className,
    compact,
    'data-testid': dataTestid,
    defaultCountry,
    description,
    disabled,
    group,
    inputClassName,
    inputVariant,
    isPending,
    label,
    placeholder,
    required,
    showChevron,
    showCode,
    showCopy,
    syncCountryWithDefault,
    variant
  }) {
    const phone = useStore(group.store, store => store.values.phone);
    const phoneCountry = useStore(group.store, store => store.values.phone_country);
    const fieldMeta = useStore(group.form.store, store => store.fieldMeta);
    const resolvedPhoneName = group.getFormFieldName('phone');
    const resolvedPhoneCountryName = group.getFormFieldName('phone_country');
    const phoneErrors = dedupeErrors([...(fieldMeta?.[resolvedPhoneName]?.errors ?? [])]);
    const phoneCountryErrors = dedupeErrors([...(fieldMeta?.[resolvedPhoneCountryName]?.errors ?? [])]);
    const [manualCountry, setManualCountry] = useState<null | string>(null);
    const isReadOnly = useReadOnly();
    useEffect(() => {
      if (!defaultCountry || manualCountry !== null || !syncCountryWithDefault) return;
      group.setFieldValue('phone_country', defaultCountry);
    }, [defaultCountry, group, manualCountry, syncCountryWithDefault]);
    const normalizedPhone = phone === null || phone === undefined ? '' : String(phone);
    const selectedCountry = toCountryCode(phoneCountry || defaultCountry);
    const advisoryPhoneError = allowInvalidPhoneSubmit && normalizedPhone ? getPhoneState(normalizedPhone, toCountryCode(phoneCountry) ?? undefined).validationMessage : undefined;
    const fieldLabel = label && typeof label === 'string' ? <FieldLabel className={cn(required && 'required gap-0.5')}>{label}</FieldLabel> : label;
    if (isReadOnly) {
      return <Field className={cn('gap-1', className)}>
          {fieldLabel}
          <ReadOnlyTooltip>
            {formatPhoneToInternational(normalizedPhone, selectedCountry ?? '') ?? '-'}
          </ReadOnlyTooltip>
        </Field>;
    }
    return <Field className={cn('group gap-1', className)}>
        <FieldContent>
          {fieldLabel}
          <group.Field name="phone" {...!allowInvalidPhoneSubmit && {
          validators: {
            onChange: ({
              value
            }: {
              value: unknown;
            }) => {
              if (!value) return undefined;
              const country = toCountryCode(group.getFieldValue('phone_country'));
              return getPhoneState(String(value), country ?? undefined).validationMessage;
            },
            onChangeListenTo: ['phone_country']
          }
        }}>
            {field => <PhoneInput className={cn('w-full', compact && '**:data-[slot=input-group-addon]:py-0')} country={selectedCountry} data-testid={dataTestid ?? 'phone-field'} disabled={disabled || isPending} inputVariant={inputVariant} onChange={e => {
            group.setFieldValue('phone', e.target.value);
          }} onCountryChange={(country: string) => {
            group.setFieldValue('phone_country', country);
            if (defaultCountry && country === defaultCountry) {
              setManualCountry(null);
            } else {
              setManualCountry(country);
            }
            if (!allowInvalidPhoneSubmit) {
              field.validate('change');
            }
          }} onGroupBlur={() => {
            field.handleBlur();
            const currentPhone = group.store.state.values.phone;
            if (!currentPhone) {
              if (defaultCountry && manualCountry === null) {
                group.setFieldValue('phone_country', defaultCountry);
              } else if (!defaultCountry && manualCountry !== null) {
                group.setFieldValue('phone_country', '');
              }
            }
          }} placeholder={placeholder} value={normalizedPhone} variant={variant}>
                <PhoneInput.CountrySelect showChevron={showChevron} showCode={showCode} />
                <PhoneInput.Field className={cn(compact && 'placeholder:text-foreground focus:placeholder:text-muted-foreground h-6 p-0 text-base!', inputClassName)} data-testid={dataTestid} onFocus={() => {
              if (!normalizedPhone && !phoneCountry && defaultCountry) {
                group.setFieldValue('phone_country', defaultCountry);
              }
            }} />
                {isPending ? <InputGroupAddon align="inline-end">
                    <Spinner className="text-muted-foreground" />
                  </InputGroupAddon> : <>
                    <PhoneInput.Action />
                    {showCopy && <PhoneInput.CopyMenu />}
                  </>}
              </PhoneInput>}
          </group.Field>
          {description && typeof description === 'string' ? <FieldDescription>{description}</FieldDescription> : description}
          <FieldError errors={dedupeErrors([...phoneErrors, ...phoneCountryErrors, ...(advisoryPhoneError ? [{
          message: advisoryPhoneError
        }] : [])])} />
        </FieldContent>
      </Field>;
  }
});

// Re-export the props type for consumers
export type PhoneFieldProps = ComponentProps<typeof PhoneField> & PhoneFieldRenderProps;